import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchFilter } from "../components/ui/SearchFilterForm";
import { apiErrorMessage } from "../utils/helpers";

/**
 * The state machine both research review screens run: a filtered, paged queue of records worked
 * one item at a time, where every accept re-reads its own card instead of re-querying the page.
 *
 * Shared rather than copied because the two screens are the same screen over different records,
 * and each piece here is a defect one of them already paid for — the silent refresh, the page
 * clamp, the read-back whose failure is reported differently from the write's, the generation
 * guard. A second copy is one that gets fixed in one place.
 *
 * `api` needs a `getAll(params)` and **must be referentially stable** — a module-level service
 * object, never an inline literal. It is a dependency of `load`, which an effect runs, so a fresh
 * identity each render is an infinite request loop rather than a re-render.
 *
 * The translation review queue (#692) runs it too, starting on its own `initialStatus` and naming
 * its failure with `loadErrorMessage`.
 *
 * `filters` are extra query params (the event screen's `city`); the caller owns their state and
 * calls `resetPage` when one changes, since a filter change that keeps the page lands out of
 * range and renders as "no records". Unlike `api` they are compared by value, so an inline
 * object is fine here. They are spread after the hook's own params, so a filter may replace one:
 * the translations page's All tab sends its status filter as `status` (#839).
 */
export function useResearchQueue({
  api,
  pageSize,
  entityLabel,
  filters = {},
  initialStatus = "open",
  loadErrorMessage = `Error loading ${entityLabel} research`
}) {
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [status, setStatus] = useState(initialStatus);
  const [page, setPage] = useState(1);
  // Submit-on-enter: typing does not re-query, and each query costs a round trip.
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(null);
  // Every read is stamped with the filter generation it was issued under. An accept can take
  // seconds, and switching tab or searching meanwhile would otherwise let the in-flight read
  // repaint the previous filter's records, and its pagination, under the new one.
  const generation = useRef(0);
  const filterKey = JSON.stringify(filters);

  // `silent` refreshes without unmounting the list: the full loading branch replaces the page
  // with a spinner, throws the scroll to the top and remounts every card — which on the event
  // screen also made each alert card re-read six third-party pages and lose its selection.
  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError("");
      const issued = ++generation.current;
      try {
        const data = await api.getAll({
          status,
          page,
          page_size: pageSize,
          ...(search ? { search } : {}),
          ...JSON.parse(filterKey)
        });
        if (issued !== generation.current) return;
        setRecords(data?.records || []);
        setPagination(data?.pagination || { page: 1, total: 0, total_pages: 0 });
      } catch (err) {
        if (issued !== generation.current) return;
        console.error(`Error loading ${entityLabel} research:`, err);
        // A silent load is always a read-back from `act`, which distinguishes "the write failed"
        // from "the write landed but the screen could not be refreshed". Swallowing it here made
        // the second message unreachable on its most likely path.
        if (silent) throw err;
        setError(apiErrorMessage(err, loadErrorMessage));
      } finally {
        // Guarded on the generation too: clearing it from a stale response dropped the page out
        // of its spinner and rendered the previous filter's records under the new tab.
        if (!silent && issued === generation.current) setLoading(false);
      }
    },
    [api, pageSize, entityLabel, loadErrorMessage, status, page, search, filterKey]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Working the queue shrinks it, so the page you are on can stop existing — resolve the last
  // record on page 2 and the pager disappears, leaving an empty screen with no way back.
  useEffect(() => {
    if (page > 1 && pagination.total_pages > 0 && page > pagination.total_pages) {
      setPage(pagination.total_pages);
    }
  }, [pagination.total_pages, page]);

  // The toast clears itself; a confirmation that stays put stops being a confirmation and starts
  // being furniture, and you cannot tell which accept it belonged to.
  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  // Re-read ONE record instead of re-querying the page, so accepting an item cannot move the card
  // being worked on — the event list ranks open image alerts first, and that key changes while an
  // admin works. `?search=` takes an exact entity id and returns every server-computed field the
  // card needs. A record the query no longer returns has left the filter, and that is the one case
  // worth a full reload: dropping it from the array alone shrinks the page and never refills it,
  // drifting `total` until the queue renders empty with open records behind it.
  const refreshRecord = useCallback(
    async (recordId, entityId) => {
      const issued = generation.current;
      const data = await api.getAll({ status, page: 1, page_size: 5, search: entityId });
      // The filter moved while this was in flight: the card is not on screen any more, and
      // writing it back would mix two filters' rows together.
      if (issued !== generation.current) return;
      const fresh = (data?.records || []).find((item) => item.id === recordId);
      if (!fresh) {
        await load({ silent: true });
        return;
      }
      setRecords((current) => current.map((item) => (item.id === recordId ? fresh : item)));
    },
    [api, status, load]
  );

  // `target` is `{recordId, entityId}` when the action belongs to one card, which is every accept
  // on these screens — the card binds it so no call site has to remember. Without it the whole
  // page re-queries and the list reshuffles under the admin. `{readBack: false}` is for a call
  // that writes nothing (a suggestion), so there is nothing to re-read.
  const act = async (key, run, message, target) => {
    if (busy) return;
    setBusy(key);
    setError("");
    let outcome;
    try {
      // A string back from `run` replaces the default message — for an action whose result the
      // admin has to know about, not merely that it succeeded (an accepted MusicBrainz id that
      // merged the artist away is the case that needs it).
      outcome = await run();
    } catch (err) {
      console.error("Error applying finding:", err);
      // A 422 carries a list of validation errors; rendered raw it blanked the page.
      setError(apiErrorMessage(err, "Error applying finding"));
      setBusy(null);
      return;
    }
    // The write landed. Everything below is a read-back, and its failure is a different fact:
    // reported as a failed action it sends the admin to retry an accept that already succeeded,
    // and the retry answers with a 409 about their own write.
    setSuccess(typeof outcome === "string" ? outcome : message);
    if (target?.readBack === false) {
      setBusy(null);
      return;
    }
    try {
      if (target) await refreshRecord(target.recordId, target.entityId);
      else await load({ silent: true });
    } catch (err) {
      console.error("Applied, but re-reading the record failed:", err);
      setError("Saved. The card could not be refreshed — reload to see its current state.");
    } finally {
      setBusy(null);
    }
  };

  const resetPage = useCallback(() => setPage(1), []);

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  // A filter change must reset the page, or submitting a search while on page 3 lands on an
  // out-of-range page of a smaller result set and renders as "no records".
  const runSearch = (event) => {
    setPage(1);
    submitSearch(event);
  };

  const resetSearch = () => {
    setPage(1);
    clearSearch();
  };

  return {
    records,
    setRecords,
    pagination,
    status,
    page,
    setPage,
    searchInput,
    setSearchInput,
    search,
    loading,
    error,
    setError,
    success,
    setSuccess,
    busy,
    setBusy,
    load,
    act,
    resetPage,
    changeStatus,
    runSearch,
    resetSearch
  };
}
